/**
 * Exam Mode replies carry fixed-prefix card lines (backend
 * chat_framework._EXAM_CARDS / EXAM_CARD_PREFIXES -- this list must match
 * it). Each exam gets its own card set; this lifts every card line out of
 * the prose so the bubble renders them as cards instead of a run-on
 * paragraph. Markers are matched anywhere, not only at line starts: models
 * sometimes run the lines together on one line.
 *
 * Returns null for a normal (non-exam) reply -- the bubble renders as before.
 */
const PARTS = [
  // Shared close, every exam.
  { key: 'time', prefix: 'Time this should take you:' },
  { key: 'trap', prefix: 'Common trap here:' },
  // JEE Main
  { key: 'contrast', prefix: 'Full derivation would take', keepMarker: true },
  { key: 'heuristic', prefix: 'If this exact pattern shows up:' }, // JEE Main + NEET calculations
  { key: 'format', prefix: 'Answer format check:' },
  // JEE Advanced
  { key: 'concepts', prefix: 'Concepts combined:' },
  { key: 'options', prefix: 'Option check:' },
  // NEET
  { key: 'ncert', prefix: 'NCERT line:' },
  { key: 'exception', prefix: 'Exception to remember:' },
  { key: 'hook', prefix: 'Memory hook:' },
  { key: 'eliminate', prefix: 'Options to eliminate:' },
  // SAT ('format' and 'heuristic' above are shared with JEE Main/NEET)
  { key: 'evidence', prefix: 'Evidence:' },
];

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const MATCHERS = PARTS.map((p) => ({
  ...p,
  // Tolerate **bold** / _italic_ wrapped around the prefix.
  re: new RegExp(`[*_]*\\s*${escapeRe(p.prefix)}${p.keepMarker ? '' : '\\s*[*_]*'}`, 'i'),
}));

const trimDecoration = (s) => s.replace(/^[\s*_]+|[\s*_]+$/g, '').trim();

export function splitExamReply(text) {
  if (!text) return null;
  const hits = [];
  for (const m of MATCHERS) {
    const found = m.re.exec(text);
    if (found) hits.push({ ...m, start: found.index, end: found.index + found[0].length });
  }
  // Only the shared close marks a reply as an Exam Mode reply -- a stray
  // "Memory hook:" in normal chat must never turn into a card.
  if (!hits.some((h) => h.key === 'time' || h.key === 'trap')) return null;

  hits.sort((a, b) => a.start - b.start);
  const parts = {};
  hits.forEach((hit, i) => {
    const stop = i + 1 < hits.length ? hits[i + 1].start : text.length;
    const value = trimDecoration(text.slice(hit.keepMarker ? hit.start : hit.end, stop));
    if (value) parts[hit.key] = value;
  });
  return { body: text.slice(0, hits[0].start).replace(/\s+$/, ''), parts };
}
